import React, { useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { LogOut, Receipt, Loader2 } from 'lucide-react';
import { openBillingPortal } from '../services/supabase';

interface HeaderProps {
  user?: User | null;
  onLogin?: () => void;
  onSignUp?: () => void;
  onSignOut?: () => void;
}

export default function Header({ user, onLogin, onSignUp, onSignOut }: HeaderProps) {
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingNotice, setBillingNotice] = useState<string | null>(null);

  const handleBillingPortal = async () => {
    if (billingLoading) return;
    setBillingNotice(null);
    setBillingLoading(true);
    try {
      const result = await openBillingPortal();
      if ('noCustomer' in result) {
        setBillingNotice("No invoices yet — buy credits or a plan and they'll appear here.");
      } else {
        window.location.href = result.url;
      }
    } catch (err) {
      console.error('[Billing] portal failed:', err);
      setBillingNotice("Couldn't open billing right now. Please try again.");
    } finally {
      setBillingLoading(false);
    }
  };

  return (
    <header className="px-6 py-4 border-b border-slate-900/80 bg-slate-950/60 backdrop-blur-md">
      <div className="flex items-center justify-between">
        <a href="/" className="flex items-center gap-2.5">
          <img src="/Icon.png" alt="" className="h-8 w-8 rounded-lg" />
          <span className="text-white font-bold text-lg tracking-tight">Valmate</span>
          <span className="text-xs text-slate-500 hidden sm:inline border-l border-slate-700 pl-2 ml-1">
            by ReHub
          </span>
        </a>

        <nav className="flex items-center gap-2">
          {user ? (
            <>
              <span className="hidden sm:inline text-xs text-slate-400 mr-2">{user.email}</span>
              <div className="relative">
                <button
                  onClick={handleBillingPortal}
                  disabled={billingLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white border border-slate-800 hover:border-slate-700 rounded-lg transition-colors disabled:opacity-60"
                >
                  {billingLoading ? <Loader2 size={13} className="animate-spin" /> : <Receipt size={13} />}
                  Billing &amp; invoices
                </button>
                {billingNotice && (
                  <div className="absolute right-0 top-full mt-1 w-56 px-3 py-2 text-xs text-amber-300/90 bg-slate-900 border border-slate-800 rounded-lg shadow-xl z-50">{billingNotice}</div>
                )}
              </div>
              <button
                onClick={onSignOut}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-white border border-slate-800 hover:border-slate-700 rounded-lg transition-colors"
              >
                <LogOut size={13} />
                Sign out
              </button>
            </>
          ) : (
            <>
              <button
                onClick={onLogin}
                className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
              >
                Sign in
              </button>
              <button
                onClick={onSignUp}
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg transition-colors"
                style={{ background: '#f47a6b' }}
                onMouseOver={(e) => (e.currentTarget.style.background = '#e56a5a')}
                onMouseOut={(e) => (e.currentTarget.style.background = '#f47a6b')}
              >
                Get started
              </button>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
